import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import WorkingHourglass from "@/components/WorkingHourglass";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowsClockwise,
  Copy,
  Crosshair,
  Lightning,
  ListChecks,
  Path,
  Question,
  ShieldWarning,
  Sparkle,
  Target,
  Trash,
  X,
} from "@phosphor-icons/react";

const NEXT_CALL_PLAN_SCHEMA_VERSION = 2;

function textOf(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return String(value.text || value.title || value.answer || value.description || "").trim();
  }
  return String(value).trim();
}

function isCurrentPlan(value) {
  return Boolean(
    value
    && typeof value === "object"
    && Number(value.schema_version || 0) >= NEXT_CALL_PLAN_SCHEMA_VERSION
  );
}

function contextualPlanFromMemory(memory) {
  const stored = memory?.contextual_prepare_next_call;
  const value = stored?.plan || stored;
  // Prompt versions govern future cache reuse, never whether a paid saved result
  // remains visible or can be selected without another AI call.
  return isCurrentPlan(value) ? value : null;
}

function preparationNotesFromMemory(memory) {
  const stored = memory?.preparation_context;
  const notes = Array.isArray(stored?.notes)
    ? stored.notes
        .map((note, index) => ({
          id: String(note?.id || `context-${index + 1}`),
          text: String(note?.text || "").trim(),
          created_at: note?.created_at || stored?.updated_at || "",
          updated_at: note?.updated_at || stored?.updated_at || "",
        }))
        .filter((note) => note.text)
    : [];
  if (notes.length) return notes;
  const legacyText = String(stored?.text || "").trim();
  return legacyText ? [{
    id: "legacy-context",
    text: legacyText,
    created_at: stored?.updated_at || "",
    updated_at: stored?.updated_at || "",
  }] : [];
}

function contextText(notes) {
  return (notes || []).map((note) => String(note?.text || "").trim()).filter(Boolean).join("\n\n");
}

function preferredPlanMode(standardPlan, contextualPlan) {
  if (!standardPlan && contextualPlan) return "additional_context";
  if (!contextualPlan) return "zero_input";
  const standardAt = new Date(standardPlan?.generated_at || 0).getTime() || 0;
  const contextualAt = new Date(contextualPlan?.generated_at || 0).getTime() || 0;
  return contextualAt > standardAt ? "additional_context" : "zero_input";
}

function createContextNote(text) {
  const id = globalThis.crypto?.randomUUID?.() || `context-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return { id, text: String(text || "").trim() };
}

function prepareGenerationFailure(error, hadPreviousPreparation) {
  const status = Number(error?.response?.status || 0);
  const detail = error?.response?.data?.detail;
  const structured = detail && typeof detail === "object" && !Array.isArray(detail) ? detail : {};
  const code = String(structured.code || "");
  const serverMessage = String(structured.message || (typeof detail === "string" ? detail : "")).trim();
  const retryAfter = Number(error?.response?.headers?.["retry-after"] || 0);
  const preserved = hadPreviousPreparation
    ? " Your current preparation is still available."
    : " Your context has been kept and nothing was saved.";

  if (!error?.response) {
    return {
      code: "network_unavailable",
      title: "Callsect could not be reached",
      body: `Check your connection and try again.${preserved}`,
      retryable: true,
    };
  }
  if (status === 429 || code === "generation_rate_limited") {
    const wait = retryAfter > 0 ? ` Try again in about ${Math.max(1, Math.ceil(retryAfter / 60))} minute${retryAfter > 60 ? "s" : ""}.` : " Try again later.";
    return {
      code: code || "generation_rate_limited",
      title: "Preparation limit reached",
      body: `${serverMessage || "You have reached the current preparation limit."}${wait}${preserved}`,
      retryable: false,
    };
  }
  if (code.startsWith("relationship_memory") || code === "analyzed_calls_missing") {
    return {
      code,
      title: "Relationship Memory needs attention",
      body: serverMessage || "Repair Relationship Memory before preparing the next call.",
      retryable: false,
    };
  }
  if (code === "generation_allowance_reached") {
    return {
      code,
      title: "Generation allowance reached",
      body: serverMessage || "Your current generation allowance has been used. Your preparation and context were not changed.",
      retryable: false,
    };
  }
  if (["incomplete_ai_output", "output_repair_failed"].includes(code)) {
    return {
      code,
      title: "The preparation was incomplete",
      body: serverMessage || `Callsect could not produce a complete preparation.${preserved}`,
      retryable: true,
    };
  }
  if (code === "ai_service_unavailable" || status >= 500) {
    return {
      code: code || "ai_service_unavailable",
      title: "The AI service did not respond",
      body: serverMessage || `Callsect could not generate the preparation right now.${preserved}`,
      retryable: true,
    };
  }
  return {
    code: code || "generation_failed",
    title: "We couldn’t create the preparation",
    body: serverMessage || `${getActionableError(error, "Callsect could not prepare the next call.")}${preserved}`,
    retryable: Boolean(structured.retryable),
  };
}

function relativeGeneratedAt(value) {
  if (!value) return "Not generated yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently updated";
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "Updated just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Updated ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Updated ${days}d ago`;
}

function BriefCard({ icon: Icon, title, children, className = "" }) {
  return (
    <article className={`prepare-next-call-card ${className}`.trim()}>
      <header className="prepare-next-call-card__header">
        <span className="prepare-next-call-card__icon" aria-hidden="true">
          <Icon size={19} weight="duotone" />
        </span>
        <h3>{title}</h3>
      </header>
      <div className="prepare-next-call-card__body">{children}</div>
    </article>
  );
}

function EmptyLine({ children }) {
  return <p className="prepare-next-call-card__empty">{children}</p>;
}

function ScriptSection({ number, title, children }) {
  return (
    <section className="prepare-next-call-script__section">
      <div className="prepare-next-call-script__section-title">
        <span>{number}</span>
        <h3>{title}</h3>
      </div>
      {children}
    </section>
  );
}

function PlanLoading({ lockClose = false }) {
  return (
    <div className="prepare-next-call-loading" role="status" aria-live="polite">
      <div className="prepare-next-call-loading__intro">
        <span className="prepare-next-call-loading__mark" aria-hidden="true">
          <Sparkle size={24} weight="fill" />
        </span>
        <div>
          <strong>Preparing a conversation you could actually have...</strong>
          <p>
            Callsect is combining relationship memory, your natural voice, and this customer’s communication pattern.
            {lockClose ? " Preparation is being generated. You can close it once it's ready." : ""}
          </p>
        </div>
      </div>

      <div className="prepare-next-call-loading__workspace" aria-hidden="true">
        <main>
          <span className="prepare-next-call-skeleton prepare-next-call-skeleton--strategy" />
          <div className="prepare-next-call-loading__pair">
            <span className="prepare-next-call-skeleton prepare-next-call-skeleton--card" />
            <span className="prepare-next-call-skeleton prepare-next-call-skeleton--card" />
          </div>
          <span className="prepare-next-call-skeleton prepare-next-call-skeleton--wide" />
          <span className="prepare-next-call-skeleton prepare-next-call-skeleton--wide" />
        </main>
        <span className="prepare-next-call-skeleton prepare-next-call-skeleton--script" />
      </div>
    </div>
  );
}

function planScriptText(plan, customerName) {
  const script = plan?.next_call_script || {};
  const lines = [`Prepare Next Call${customerName ? ` · ${customerName}` : ""}`];
  const strategy = textOf(plan?.conversation_strategy);
  if (strategy) lines.push(`\nPlan\n${strategy}`);
  const opening = textOf(script.opening);
  if (opening) lines.push(`\nOpening\n${opening}`);
  (script.mid_call_pivots || []).forEach((pivot, index) => {
    const trigger = textOf(pivot?.trigger);
    const text = textOf(pivot?.text);
    if (!text) return;
    lines.push(`\n${trigger || `If the conversation shifts (${index + 1})`}\n${text}`);
  });
  const closing = textOf(script.closing);
  if (closing) lines.push(`\nClosing\n${closing}`);
  const keepInMind = textOf(script.keep_in_mind || script.pro_tip);
  if (keepInMind) lines.push(`\nKeep in mind\n${keepInMind}`);
  return lines.join("\n").trim();
}

export default function PrepareNextCall({
  memory,
  customer,
  milestones = [],
  demoMode = false,
  openOnMount = false,
  showPreview = true,
  onClose,
}) {
  const storedStandardPlan = isCurrentPlan(memory?.prepare_next_call) ? memory.prepare_next_call : null;
  const storedContextualPlan = contextualPlanFromMemory(memory);
  const storedContextNotes = preparationNotesFromMemory(memory);
  const [standardPlan, setStandardPlan] = useState(storedStandardPlan);
  const [contextualPlan, setContextualPlan] = useState(storedContextualPlan);
  const [planMode, setPlanMode] = useState(preferredPlanMode(storedStandardPlan, storedContextualPlan));
  const [contextNotes, setContextNotes] = useState(storedContextNotes);
  const [contextDraft, setContextDraft] = useState("");
  const [editingContextId, setEditingContextId] = useState("");
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [clearingContext, setClearingContext] = useState(false);
  const [generationMode, setGenerationMode] = useState("generate");
  const generationLockRef = useRef(false);
  const [error, setError] = useState("");
  const [contextError, setContextError] = useState("");
  const [generationFailure, setGenerationFailure] = useState(null);
  const [failedGenerationRequest, setFailedGenerationRequest] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [contextEntryOpen, setContextEntryOpen] = useState(false);
  const drawerRef = useRef(null);
  const openOnMountHandledRef = useRef(false);
  const plan = planMode === "additional_context" ? contextualPlan : standardPlan;

  useEffect(() => {
    const nextStandardPlan = isCurrentPlan(memory?.prepare_next_call) ? memory.prepare_next_call : null;
    const nextContextualPlan = contextualPlanFromMemory(memory);
    setStandardPlan(nextStandardPlan);
    setContextualPlan(nextContextualPlan);
    setContextNotes(preparationNotesFromMemory(memory));
    setPlanMode((currentMode) => {
      if (currentMode === "additional_context" && nextContextualPlan) return currentMode;
      if (currentMode === "zero_input" && nextStandardPlan) return currentMode;
      return preferredPlanMode(nextStandardPlan, nextContextualPlan);
    });
    setContextDraft("");
    setEditingContextId("");
  }, [memory?.prepare_next_call, memory?.contextual_prepare_next_call, memory?.preparation_context]);

  const operationLocked = generating || clearingContext;
  const additionalContext = contextText(contextNotes);

  useEffect(() => {
    if ((!open && !confirmation && !generationFailure) || typeof document === "undefined") return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const focusFrame = open ? window.requestAnimationFrame(() => {
      drawerRef.current?.focus({ preventScroll: true });
      const scrollRegion = drawerRef.current?.querySelector(
        ".prepare-next-call-drawer__content, .prepare-next-call-loading, .prepare-next-call-error"
      );
      if (scrollRegion) scrollRegion.scrollTop = 0;
    }) : null;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (generationFailure) {
        setGenerationFailure(null);
        return;
      }
      if (confirmation) {
        if (operationLocked) return;
        setContextDraft("");
        setEditingContextId("");
        setContextEntryOpen(false);
        setConfirmation(null);
        return;
      }
      if (operationLocked) return;
      setOpen(false);
      onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      if (focusFrame != null) window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, confirmation, generationFailure, operationLocked, onClose, additionalContext]);

  const callCount = Number(plan?.source_call_count || customer?.call_count || milestones.length || 0);
  const relationshipId = customer?.id;

  const generatePlan = async ({ force = false, notes = null, context = "", failureKind = "generate", noteId = "" } = {}) => {
    if (generationLockRef.current) return null;
    const requestedNotes = Array.isArray(notes)
      ? notes.map((note) => ({ id: String(note?.id || ""), text: String(note?.text || "").trim() })).filter((note) => note.text)
      : String(context || "").trim() ? [createContextNote(context)] : [];
    const normalizedContext = contextText(requestedNotes);
    const contextual = Boolean(normalizedContext);
    if (demoMode) {
      const demoPlan = isCurrentPlan(memory?.prepare_next_call) ? memory.prepare_next_call : standardPlan;
      setStandardPlan(demoPlan);
      setPlanMode("zero_input");
      return demoPlan;
    }
    if (!relationshipId) {
      const message = "Callsect could not resolve this relationship.";
      setError(message);
      toast.error(message);
      return null;
    }
    generationLockRef.current = true;
    const currentPlan = contextual ? contextualPlan : standardPlan;
    setGenerationMode(currentPlan ? "regenerate" : "generate");
    setGenerating(true);
    setError("");
    setGenerationFailure(null);
    try {
      const payload = { force };
      if (contextual) {
        payload.additional_context = normalizedContext;
        payload.context_notes = requestedNotes;
      }
      const { data } = await api.post(
        `/customers/${encodeURIComponent(relationshipId)}/next-call/prepare`,
        payload
      );
      if (contextual) {
        setContextualPlan(data);
        setContextNotes(requestedNotes);
        setContextDraft("");
        setEditingContextId("");
        setPlanMode("additional_context");
      } else {
        setStandardPlan(data);
        setPlanMode("zero_input");
      }
      toast.success(contextual ? "Context-aware brief ready" : force ? "Next-call brief regenerated" : "Next-call brief ready");
      setFailedGenerationRequest(null);
      return data;
    } catch (err) {
      const message = getActionableError(err, "Callsect could not prepare the next call.");
      setError(message);
      setFailedGenerationRequest({ force, notes: requestedNotes, context, failureKind, noteId });
      setGenerationFailure(prepareGenerationFailure(err, Boolean(currentPlan)));
      return null;
    } finally {
      setGenerating(false);
      generationLockRef.current = false;
    }
  };

  const requestGeneration = (kind = "generate", noteId = "") => {
    if (demoMode) {
      setOpen(true);
      if (!plan) void generatePlan();
      else if (kind === "regenerate") void generatePlan({ force: true });
      return;
    }
    setContextError("");
    const noteToEdit = kind === "edit_context"
      ? contextNotes.find((note) => note.id === noteId)
      : null;
    setEditingContextId(noteToEdit?.id || "");
    setContextDraft(noteToEdit?.text || "");
    setContextEntryOpen(["contextual", "add_context", "edit_context"].includes(kind));
    setConfirmation({ kind, mode: planMode, noteId: noteToEdit?.id || noteId || "" });
  };

  const openFullBrief = () => {
    const initialMode = preferredPlanMode(standardPlan, contextualPlan);
    setPlanMode(initialMode);
    if (standardPlan || contextualPlan || demoMode) {
      setOpen(true);
      if (!standardPlan && demoMode) void generatePlan();
      return;
    }
    requestGeneration(contextNotes.length ? "regenerate_contextual" : "generate");
  };

  useEffect(() => {
    if (!openOnMount || openOnMountHandledRef.current) return;
    openOnMountHandledRef.current = true;
    openFullBrief();
    // This launcher behavior is intentionally one-shot per mounted customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOnMount]);

  const closeFullBrief = () => {
    if (operationLocked) return;
    setOpen(false);
    onClose?.();
  };

  const rollbackContextNote = async (noteId) => {
    if (!relationshipId || !noteId || demoMode || clearingContext) return;
    setClearingContext(true);
    setContextError("");
    try {
      const { data } = await api.delete(
        `/customers/${encodeURIComponent(relationshipId)}/next-call/context/${encodeURIComponent(noteId)}`
      );
      const restoredPlan = data?.plan || data?.contextual_prepare_next_call?.plan || null;
      const restoredNotes = Array.isArray(data?.context_notes)
        ? data.context_notes.map((note, index) => ({
            id: String(note?.id || `context-${index + 1}`),
            text: String(note?.text || "").trim(),
            created_at: note?.created_at || "",
            updated_at: note?.updated_at || "",
          })).filter((note) => note.text)
        : [];
      const usableRestoredPlan = isCurrentPlan(restoredPlan) ? restoredPlan : null;
      setContextNotes(restoredNotes);
      setContextualPlan(usableRestoredPlan);
      setPlanMode(usableRestoredPlan ? "additional_context" : "zero_input");
      setContextDraft("");
      setEditingContextId("");
      setContextEntryOpen(false);
      setConfirmation(null);
      setOpen(Boolean(usableRestoredPlan || standardPlan));
      toast.success(usableRestoredPlan
        ? "Returned to the previous preparation — no AI generation used"
        : "Context removed — no AI generation used");
    } catch (err) {
      const message = getActionableError(err, "The previous preparation could not be restored.");
      setContextError(message);
      toast.error(message);
    } finally {
      setClearingContext(false);
    }
  };

  const confirmGeneration = async () => {
    const kind = confirmation?.kind;
    if (!kind) return;

    if (kind === "remove_context_note") {
      await rollbackContextNote(confirmation?.noteId);
      return;
    }

    if (kind === "regenerate_contextual") {
      if (!contextNotes.length) {
        setContextEntryOpen(true);
        setConfirmation({ kind: "add_context", mode: "additional_context" });
        setContextError("Add the private context Callsect should take into account.");
        return;
      }
      setContextEntryOpen(false);
      setConfirmation(null);
      setOpen(true);
      await generatePlan({ force: true, notes: contextNotes, failureKind: kind });
      return;
    }

    if (["contextual", "add_context", "edit_context"].includes(kind)) {
      const normalizedContext = contextDraft.trim();
      if (!normalizedContext) {
        setContextError("Add the private context Callsect should take into account.");
        return;
      }
      const previousNote = contextNotes.find((note) => note.id === confirmation?.noteId);
      if (kind === "edit_context" && normalizedContext === previousNote?.text) return;
      const nextNotes = kind === "edit_context"
        ? contextNotes.map((note) => note.id === confirmation?.noteId ? { ...note, text: normalizedContext } : note)
        : [...contextNotes, createContextNote(normalizedContext)];
      setContextEntryOpen(false);
      setConfirmation(null);
      setOpen(true);
      await generatePlan({
        force: Boolean(contextualPlan) || kind !== "contextual",
        notes: nextNotes,
        failureKind: kind,
        noteId: confirmation?.noteId || "",
      });
      return;
    }

    const optionalContext = contextEntryOpen ? contextDraft.trim() : "";
    if (optionalContext) {
      const nextNotes = [...contextNotes, createContextNote(optionalContext)];
      setContextEntryOpen(false);
      setConfirmation(null);
      setOpen(true);
      await generatePlan({ force: Boolean(contextualPlan), notes: nextNotes, failureKind: kind });
      return;
    }

    setContextEntryOpen(false);
    setConfirmation(null);
    setOpen(true);
    await generatePlan({ force: kind === "regenerate", failureKind: kind });
  };

  const clearAdditionalContext = async () => {
    if (!relationshipId || demoMode || clearingContext) return;
    setClearingContext(true);
    setContextError("");
    try {
      await api.delete(`/customers/${encodeURIComponent(relationshipId)}/next-call/context`);
      setContextNotes([]);
      setContextDraft("");
      setEditingContextId("");
      setContextualPlan(null);
      setPlanMode("zero_input");
      setContextEntryOpen(false);
      setConfirmation(null);
      if (!standardPlan) {
        setOpen(false);
        onClose?.();
      }
      toast.success("Private context and contextual preparation deleted");
    } catch (err) {
      const message = getActionableError(err, "The private context and preparation could not be deleted.");
      setContextError(message);
      toast.error(message);
    } finally {
      setClearingContext(false);
    }
  };

  const retryFailedGeneration = async () => {
    if (!failedGenerationRequest || generating) return;
    const request = failedGenerationRequest;
    setGenerationFailure(null);
    setOpen(true);
    await generatePlan(request);
  };

  const reviewFailedContext = () => {
    const request = failedGenerationRequest;
    if (!request?.notes?.length) return;
    const editing = ["edit_context", "regenerate_contextual"].includes(request.failureKind);
    const draftNote = editing
      ? request.notes.find((note) => note.id === request.noteId) || request.notes[request.notes.length - 1]
      : request.notes[request.notes.length - 1];
    setContextDraft(draftNote?.text || "");
    setEditingContextId(editing ? draftNote?.id || "" : "");
    setContextEntryOpen(true);
    setConfirmation({
      kind: editing ? "edit_context" : (contextNotes.length ? "add_context" : "contextual"),
      mode: "additional_context",
      noteId: editing ? draftNote?.id || "" : "",
    });
    setGenerationFailure(null);
  };

  const isContextConfirmation = ["contextual", "add_context", "edit_context"].includes(confirmation?.kind);
  const isEditingContext = confirmation?.kind === "edit_context";
  const isDeleteContextConfirmation = confirmation?.kind === "delete_context";
  const isRemoveContextNoteConfirmation = confirmation?.kind === "remove_context_note";
  const removingContextIndex = contextNotes.findIndex((note) => note.id === confirmation?.noteId);
  const discardedContextCount = removingContextIndex >= 0 ? contextNotes.length - removingContextIndex : 0;
  const canAddContextFromConfirmation = !contextNotes.length && ["generate", "regenerate"].includes(confirmation?.kind);
  const showContextField = isContextConfirmation || (canAddContextFromConfirmation && contextEntryOpen);
  const hasOptionalContext = canAddContextFromConfirmation && contextEntryOpen && Boolean(contextDraft.trim());
  const editedContextNote = contextNotes.find((note) => note.id === editingContextId);
  const contextDraftChanged = contextDraft.trim() !== String(editedContextNote?.text || "").trim();
  const contextCharactersOutsideDraft = contextNotes
    .filter((note) => note.id !== editingContextId)
    .reduce((total, note) => total + note.text.length, 0);
  const resultingContextNoteCount = isEditingContext ? contextNotes.length : contextNotes.length + 1;
  const contextDraftLimit = Math.max(
    0,
    4000 - contextCharactersOutsideDraft - Math.max(0, resultingContextNoteCount - 1) * 2
  );
  const confirmationDetails = isRemoveContextNoteConfirmation
    ? {
        title: removingContextIndex > 0 ? "Return to the previous preparation?" : "Remove all contextual preparation?",
        body: removingContextIndex > 0
          ? `Callsect will return to the saved preparation from before this note was added.${discardedContextCount > 1 ? ` This also removes the ${discardedContextCount - 1} later context note${discardedContextCount - 1 === 1 ? "" : "s"} built on top of it.` : ""} No AI generation or tokens will be used.`
          : "This removes the first context note, all later context notes, and the contextual preparation. No AI generation or tokens will be used.",
        confirmLabel: removingContextIndex > 0 ? "Return to previous prep" : "Remove contextual prep",
      }
    : isDeleteContextConfirmation
    ? {
        title: "Delete all context?",
        body: `All active context notes and the private-context preparation currently on screen will be deleted. ${standardPlan ? "You'll return to your standard preparation." : "You'll return to the starting screen, where you can create a new standard preparation."}`,
        confirmLabel: "Delete all context and preparation",
      }
    : confirmation?.kind === "regenerate"
    ? {
        title: "Regenerate this preparation?",
        body: "This will replace the current plan with a newly generated one.",
        confirmLabel: "Regenerate",
      }
    : confirmation?.kind === "regenerate_contextual"
      ? {
          title: "Regenerate private-context preparation?",
          body: "Callsect will create a new version using the saved private context and the current Relationship Memory.",
          confirmLabel: "Regenerate",
        }
    : isContextConfirmation
        ? {
            title: isEditingContext ? "Edit context" : "Add context",
            body: isEditingContext
              ? "Changing this context requires regenerating the preparation. Your current version will remain available if regeneration fails."
              : contextNotes.length
                ? "Callsect will build on your existing private context. This note will be added to it, not replace it, and all active notes will shape the new preparation."
                : "Callsect will combine this with the relationship history it already knows.",
            confirmLabel: isEditingContext ? "Save & regenerate" : "Add context",
          }
      : {
          title: `Prepare next call${customer?.name ? ` for ${customer.name}` : ""}?`,
          body: "Callsect will generate a new preparation using this relationship history and your Speaking DNA.",
          confirmLabel: "Prepare next call",
        };

  const copyScript = async () => {
    const content = planScriptText(plan, customer?.name);
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Next-call script copied");
    } catch {
      toast.error("The script could not be copied automatically.");
    }
  };

  const previewCopy = "Get a concise call strategy and spoken script shaped by this relationship and your natural voice.";
  const strategy = textOf(plan?.conversation_strategy) || textOf(plan?.objective) || "Clarify the next commercial move before discussing commitment.";

  return (
    <>
      {showPreview ? (
      <article id="next-call" className="prepare-next-call-preview" data-testid="prepare-next-call-preview">
        <div className="prepare-next-call-preview__identity">
          <span className="prepare-next-call-preview__icon" aria-hidden="true">
            <Target size={22} weight="duotone" />
          </span>
          <div className="prepare-next-call-preview__copy">
            <h2>Prepare Next Call</h2>
            <p>{previewCopy}</p>
          </div>
        </div>

        <div className="prepare-next-call-preview__actions">
          <button
            type="button"
            className="prepare-next-call-preview__action"
            onClick={openFullBrief}
            disabled={generating && !open}
          >
            {generating && !open ? "Preparing..." : standardPlan || contextualPlan ? "Open full brief" : "Prepare next call"}
            {generating && !open ? <WorkingHourglass size={15} /> : <ArrowRight size={15} weight="bold" />}
          </button>
        </div>
      </article>
      ) : null}

      {open && typeof document !== "undefined" ? createPortal(
        <div className="prepare-next-call-overlay">
          <section
            ref={drawerRef}
            className={`prepare-next-call-drawer${demoMode ? " prepare-next-call-drawer--demo" : " prepare-next-call-drawer--authenticated"}`}
            role="dialog"
            tabIndex={-1}
            aria-modal="true"
            aria-labelledby="prepare-next-call-title"
            aria-busy={generating}
          >
            <header className="prepare-next-call-drawer__header">
              <div className="prepare-next-call-drawer__identity">
                <span className="prepare-next-call-drawer__mark" aria-hidden="true">
                  <Target size={24} weight="duotone" />
                </span>
                <div>
                  <h2 id="prepare-next-call-title">Prepare Next Call</h2>
                  <p>Your relationship context, turned into a clear strategy and words you could actually say.</p>
                </div>
              </div>

              <div className="prepare-next-call-drawer__actions">
                {plan ? (
                  <span className="prepare-next-call-updated">
                    {generating ? <WorkingHourglass size={14} /> : <ArrowsClockwise size={14} />}
                    {generating ? "Updating preparation..." : relativeGeneratedAt(plan.generated_at)}
                  </span>
                ) : null}
                {!demoMode && standardPlan && contextualPlan ? (
                  <div className="prepare-next-call-mode-switch" role="group" aria-label="Preparation mode">
                    <button
                      type="button"
                      className={planMode === "zero_input" ? "is-active" : ""}
                      onClick={() => setPlanMode("zero_input")}
                      disabled={operationLocked}
                      aria-pressed={planMode === "zero_input"}
                      title="View the standard zero-input preparation"
                    >
                      Standard
                    </button>
                    <button
                      type="button"
                      className={planMode === "additional_context" ? "is-active" : ""}
                      onClick={() => setPlanMode("additional_context")}
                      disabled={operationLocked}
                      aria-pressed={planMode === "additional_context"}
                      title="View the preparation that uses saved context"
                    >
                      Private context
                    </button>
                  </div>
                ) : !demoMode && plan ? (
                  <span className="prepare-next-call-mode-label">
                    {planMode === "additional_context" ? "Private context" : "Standard"}
                  </span>
                ) : null}
                {!demoMode && standardPlan && !contextualPlan ? (
                  <button
                    type="button"
                    className="prepare-next-call-secondary-action"
                    onClick={() => requestGeneration(contextNotes.length ? "regenerate_contextual" : "add_context")}
                    disabled={operationLocked}
                  >
                    <Sparkle size={15} />
                    {contextNotes.length ? "Regenerate private-context version" : "Add context"}
                  </button>
                ) : null}
                {!demoMode && contextualPlan && !standardPlan ? (
                  <button
                    type="button"
                    className="prepare-next-call-secondary-action"
                    onClick={() => requestGeneration("generate")}
                    disabled={operationLocked}
                  >
                    Create standard version
                  </button>
                ) : null}
                <button
                  type="button"
                  className="prepare-next-call-regenerate"
                  onClick={() => requestGeneration(
                    planMode === "additional_context"
                      ? "regenerate_contextual"
                      : plan ? "regenerate" : "generate"
                  )}
                  disabled={operationLocked}
                  title={planMode === "additional_context" ? "Regenerate using the saved private context" : "Regenerate the standard preparation"}
                >
                  {generating ? <WorkingHourglass size={15} /> : <Sparkle size={15} weight="fill" />}
                  {generating
                    ? generationMode === "regenerate" ? "Regenerating..." : "Generating..."
                    : plan ? "Regenerate current prep" : "Generate prep"}
                </button>
                {!operationLocked ? (
                  <button
                    type="button"
                    className="prepare-next-call-drawer__close"
                    onClick={closeFullBrief}
                    aria-label="Close Prepare Next Call"
                  >
                    <X size={20} />
                  </button>
                ) : null}
              </div>
            </header>

            {generating && !plan ? (
              <PlanLoading lockClose={!demoMode} />
            ) : error && !plan ? (
              <div className="prepare-next-call-error" role="alert">
                <ShieldWarning size={26} weight="duotone" />
                <strong>The brief could not be prepared.</strong>
                <p>{error}</p>
                <button type="button" onClick={() => requestGeneration("generate")}>Try again</button>
              </div>
            ) : plan ? (
              <div className="prepare-next-call-drawer__content">
                {planMode === "additional_context" && additionalContext ? (
                  <section className="prepare-next-call-private-context" aria-label="Private preparation context">
                    <div className="prepare-next-call-private-context__body">
                      <span className="prepare-next-call-private-context__eyebrow">
                        <Sparkle size={16} weight="fill" />
                        <strong>Private context</strong>
                        <small>· {contextNotes.length} note{contextNotes.length === 1 ? "" : "s"}</small>
                      </span>
                      <div className="prepare-next-call-private-context__notes">
                        {contextNotes.map((note, index) => (
                          <article key={note.id}>
                            <small>Context {index + 1}</small>
                            <p>{note.text}</p>
                            <div>
                              <button type="button" onClick={() => requestGeneration("edit_context", note.id)} disabled={operationLocked}>
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setContextError("");
                                  setConfirmation({ kind: "remove_context_note", mode: "additional_context", noteId: note.id });
                                }}
                                disabled={operationLocked}
                              >
                                Remove
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                      <small>
                        All notes shown are active only for this preparation and are not added to Relationship Memory.
                        If new information replaces an earlier instruction, edit or remove the older note first.
                      </small>
                    </div>
                    <div className="prepare-next-call-private-context__actions">
                      <button
                        type="button"
                        onClick={() => requestGeneration("add_context")}
                        disabled={operationLocked || additionalContext.length >= 3998}
                        title={additionalContext.length >= 3998 ? "Edit or remove an existing note before adding more context" : "Add another context note"}
                      >
                        Add context
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setContextError("");
                          setContextDraft("");
                          setContextEntryOpen(false);
                          setConfirmation({ kind: "delete_context" });
                        }}
                        disabled={operationLocked}
                      >
                        Delete all context &amp; prep
                      </button>
                    </div>
                  </section>
                ) : null}
                <div className="prepare-next-call-drawer__workspace">
                  <main className="prepare-next-call-brief">
                    <section className="prepare-next-call-strategy" aria-labelledby="prepare-next-call-strategy-title">
                      <span className="prepare-next-call-strategy__eyebrow">
                        <Path size={16} weight="fill" />
                        Plan
                      </span>
                      <strong id="prepare-next-call-strategy-title">{strategy}</strong>
                    </section>

                    <div className="prepare-next-call-brief__pair">
                      <BriefCard icon={Crosshair} title="Your Goal" className="prepare-next-call-card--objective">
                        <p className="prepare-next-call-objective">{textOf(plan.objective) || "Clarify the next commercial step."}</p>
                      </BriefCard>

                      <BriefCard icon={ShieldWarning} title="Likely objections">
                        {plan.likely_objections?.length ? (
                          <div className="prepare-next-call-objections">
                            {plan.likely_objections.map((item, index) => (
                              <div key={`${item.text}-${index}`}>
                                <strong>{item.text}</strong>
                                {item.response_direction ? <small>{item.response_direction}</small> : null}
                              </div>
                            ))}
                          </div>
                        ) : <EmptyLine>No recurring objection is strong enough to predict.</EmptyLine>}
                      </BriefCard>
                    </div>

                    <BriefCard icon={ListChecks} title="Call flow tips" className="prepare-next-call-card--flow">
                      {plan.suggested_call_flow?.length ? (
                        <ol className="prepare-next-call-flow">
                          {plan.suggested_call_flow.map((item, index) => (
                            <li key={`${item.title}-${index}`}>
                              <span>{index + 1}</span>
                              <div>
                                <strong>{item.title}</strong>
                                {item.purpose ? <small>{item.purpose}</small> : null}
                              </div>
                            </li>
                          ))}
                        </ol>
                      ) : <EmptyLine>No call flow tips could be grounded yet.</EmptyLine>}
                    </BriefCard>

                    <BriefCard icon={Question} title="Questions to ask" className="prepare-next-call-card--questions">
                      {plan.questions_to_ask?.length ? (
                        <ol className="prepare-next-call-questions">
                          {plan.questions_to_ask.map((item, index) => (
                            <li key={`${item.text}-${index}`}>
                              <span>{index + 1}</span>
                              <div>
                                <strong>{item.text}</strong>
                                {item.why ? <small>{item.why}</small> : null}
                              </div>
                            </li>
                          ))}
                        </ol>
                      ) : <EmptyLine>No additional question is required by the stored context.</EmptyLine>}
                    </BriefCard>
                  </main>

                  <aside className="prepare-next-call-script">
                    <header className="prepare-next-call-script__header">
                      <h2>Next-call script</h2>
                      <button type="button" onClick={copyScript}>
                        <Copy size={15} />
                        Copy script
                      </button>
                    </header>

                    <div className="prepare-next-call-script__body">
                      <ScriptSection number="1" title="Opening">
                        <p>{textOf(plan.next_call_script?.opening) || "No opening generated."}</p>
                      </ScriptSection>

                      <ScriptSection number="2" title="If the conversation shifts…">
                        {plan.next_call_script?.mid_call_pivots?.length ? (
                          <div className="prepare-next-call-script__pivots">
                            {plan.next_call_script.mid_call_pivots.map((pivot, index) => (
                              <div key={`${pivot.text}-${index}`}>
                                {pivot.trigger ? <small>{pivot.trigger}</small> : null}
                                <p>{pivot.text}</p>
                              </div>
                            ))}
                          </div>
                        ) : <p>No specific response branch is supported yet.</p>}
                      </ScriptSection>

                      <ScriptSection number="3" title="Closing">
                        <p>{textOf(plan.next_call_script?.closing) || "No closing generated."}</p>
                      </ScriptSection>

                      {textOf(plan.next_call_script?.keep_in_mind || plan.next_call_script?.pro_tip) ? (
                        <section className="prepare-next-call-script__tip">
                          <Lightning size={17} weight="fill" />
                          <div>
                            <strong>Keep in mind</strong>
                            <p>{textOf(plan.next_call_script?.keep_in_mind || plan.next_call_script?.pro_tip)}</p>
                          </div>
                        </section>
                      ) : null}

                      <footer className="prepare-next-call-script__source">
                        {demoMode
                          ? <>Built from {callCount || 1} previous call{callCount === 1 ? "" : "s"} and your current Speaking DNA.</>
                          : planMode === "additional_context"
                            ? <>Built from {callCount || 1} previous call{callCount === 1 ? "" : "s"}, your Speaking DNA, and the private context you added.</>
                            : <>Built from {callCount || 1} previous call{callCount === 1 ? "" : "s"} and your Speaking DNA, learned from your calls.</>}
                      </footer>
                    </div>
                  </aside>
                </div>
              </div>
            ) : (
              <PlanLoading lockClose={!demoMode} />
            )}
          </section>
        </div>,
        document.body
      ) : null}

      {generationFailure && typeof document !== "undefined" ? createPortal(
        <div className="prepare-next-call-confirm-overlay">
          <section
            className="prepare-next-call-confirm prepare-next-call-confirm--failure"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="prepare-next-call-failure-title"
            aria-describedby="prepare-next-call-failure-copy"
          >
            <span className="prepare-next-call-confirm__icon prepare-next-call-confirm__icon--failure" aria-hidden="true">
              <ShieldWarning size={22} weight="duotone" />
            </span>
            <div className="prepare-next-call-confirm__copy">
              <h3 id="prepare-next-call-failure-title">{generationFailure.title}</h3>
              <p id="prepare-next-call-failure-copy">{generationFailure.body}</p>
            </div>
            <div className="prepare-next-call-confirm__actions">
              <button
                type="button"
                className="prepare-next-call-confirm__cancel"
                onClick={() => {
                  setGenerationFailure(null);
                  setError("");
                  if (!standardPlan && !contextualPlan) {
                    setOpen(false);
                    onClose?.();
                  }
                }}
              >
                {standardPlan || contextualPlan ? "Keep current preparation" : "Close"}
              </button>
              {failedGenerationRequest?.notes?.length ? (
                <button
                  type="button"
                  className="prepare-next-call-confirm__cancel"
                  onClick={reviewFailedContext}
                >
                  Review context
                </button>
              ) : null}
              {generationFailure.retryable ? (
                <button
                  type="button"
                  className="prepare-next-call-confirm__submit"
                  onClick={retryFailedGeneration}
                >
                  Try again
                </button>
              ) : null}
            </div>
          </section>
        </div>,
        document.body
      ) : null}

      {confirmation && typeof document !== "undefined" ? createPortal(
        <div className="prepare-next-call-confirm-overlay">
          <section
            className="prepare-next-call-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="prepare-next-call-confirm-title"
            aria-describedby="prepare-next-call-confirm-copy"
          >
            <span className="prepare-next-call-confirm__icon" aria-hidden="true">
              {isDeleteContextConfirmation || isRemoveContextNoteConfirmation
                ? <Trash size={21} weight="fill" />
                : <Sparkle size={21} weight="fill" />}
            </span>
            <div className="prepare-next-call-confirm__copy">
              <h3 id="prepare-next-call-confirm-title">{confirmationDetails.title}</h3>
              <p id="prepare-next-call-confirm-copy">{confirmationDetails.body}</p>
            </div>
            {canAddContextFromConfirmation && !contextEntryOpen ? (
              <button
                type="button"
                className="prepare-next-call-confirm__context-toggle"
                onClick={() => {
                  setContextError("");
                  setContextDraft("");
                  setContextEntryOpen(true);
                }}
              >
                <span>
                  <Sparkle size={15} />
                  Is there anything Callsect should know?
                </span>
                <strong>Add context</strong>
              </button>
            ) : null}
            {showContextField ? (
              <div className="prepare-next-call-confirm__context-field">
                <div className="prepare-next-call-confirm__context-heading">
                  <label htmlFor="prepare-next-call-private-context">
                    Is there anything Callsect should know before preparing you?
                  </label>
                  {!isContextConfirmation ? (
                    <button
                      type="button"
                      onClick={() => {
                        setContextEntryOpen(false);
                        setContextError("");
                      }}
                    >
                      Use standard preparation
                    </button>
                  ) : null}
                </div>
                <textarea
                  id="prepare-next-call-private-context"
                  value={contextDraft}
                  onChange={(event) => {
                    setContextDraft(event.target.value);
                    if (contextError) setContextError("");
                  }}
                  maxLength={contextDraftLimit}
                  rows={5}
                  placeholder="For example: I agreed an internal price range with my manager, and I want to anchor the conversation at..."
                  autoFocus
                />
                <small>
                  Private. Callsect combines this with the relationship history, but does not add it to Relationship Memory.
                  <span>{contextDraft.length}/{contextDraftLimit}</span>
                </small>
                {contextError ? <em role="alert">{contextError}</em> : null}
                {isEditingContext && !contextDraft.trim() ? (
                  <button
                    type="button"
                    className="prepare-next-call-confirm__context-delete"
                    onClick={() => {
                      setContextError("");
                      setContextEntryOpen(false);
                      setConfirmation({
                        kind: "remove_context_note",
                        mode: "additional_context",
                        noteId: editingContextId,
                      });
                    }}
                  >
                    Remove this note instead
                  </button>
                ) : null}
              </div>
            ) : null}
            {!showContextField && contextError ? (
              <p className="prepare-next-call-confirm__error" role="alert">{contextError}</p>
            ) : null}
            <div className="prepare-next-call-confirm__actions">
              <button
                type="button"
                className="prepare-next-call-confirm__cancel"
                onClick={() => {
                  setContextDraft("");
                  setEditingContextId("");
                  setContextEntryOpen(false);
                  setConfirmation(null);
                }}
                disabled={operationLocked}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`prepare-next-call-confirm__submit${isDeleteContextConfirmation || isRemoveContextNoteConfirmation ? " prepare-next-call-confirm__submit--danger" : ""}`}
                onClick={isDeleteContextConfirmation ? clearAdditionalContext : confirmGeneration}
                disabled={
                  operationLocked
                  || (isContextConfirmation && !contextDraft.trim())
                  || (isEditingContext && !contextDraftChanged)
                }
              >
                {clearingContext
                  ? isRemoveContextNoteConfirmation ? "Returning..." : "Deleting..."
                  : generating && isEditingContext
                    ? "Regenerating..."
                    : hasOptionalContext
                  ? "Add context"
                  : confirmationDetails.confirmLabel}
              </button>
            </div>
          </section>
        </div>,
        document.body
      ) : null}
    </>
  );
}
